import React, { useState, useContext, useEffect, useRef } from "react";
import {
  makeStyles,
  InputBase,
  Tabs,
  Tab,
  Badge,
  Fab,
} from "@material-ui/core";
import {
  Search as SearchIcon,
  Add as AddIcon,
} from "@material-ui/icons";

import TicketsList from "../../components/TicketsListCustom";
import NewTicketModal from "../../components/NewTicketModal";
import TabPanel from "../../components/TabPanel";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
    backgroundColor: theme.palette.background.default,
  },
  searchContainer: {
    padding: "8px 12px",
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  searchInput: {
    width: "100%",
    backgroundColor: theme.mode === "light" ? "#f1f3f4" : "#424242",
    borderRadius: 20,
    padding: "6px 16px",
    fontSize: 14,
  },
  tabsContainer: {
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    "& .MuiTabs-indicator": {
      height: 3,
      borderRadius: "3px 3px 0 0",
    },
    "& .MuiTab-root": {
      minHeight: 44,
      minWidth: "auto",
      padding: "6px 12px",
      fontSize: "0.75rem",
      textTransform: "none",
      fontWeight: 600,
    },
  },
  ticketListWrapper: {
    flex: 1,
    overflow: "hidden",
    overscrollBehavior: "contain",
    position: "relative",
  },
  fab: {
    position: "fixed",
    bottom: 72,
    right: 16,
    zIndex: 1100,
  },
  tabBadge: {
    "& .MuiBadge-badge": {
      fontSize: "0.65rem",
      minWidth: 18,
      height: 18,
      padding: "0 4px",
    },
  },
}));

const MobileTicketList = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { tabOpen, setTabOpen } = useContext(TicketsContext);
  const userQueueIds = user?.queues?.map((q) => q.id) || [];
  const [selectedQueueIds] = useState(userQueueIds);

  const [searchParam, setSearchParam] = useState("");
  const [tab, setTab] = useState("open");
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [openCount, setOpenCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [groupCount, setGroupCount] = useState(0);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (tabOpen === "search") {
      setTab("search");
    } else {
      setTab("open");
    }
  }, [tabOpen]);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchParam(value);
    if (value) {
      setTab("search");
    } else {
      setTab("open");
    }
  };

  const handleChangeTabOpen = (e, newValue) => {
    setTabOpen(newValue);
  };

  const applyPanelStyle = (status) => {
    if (tabOpen !== status) {
      return { width: 0, height: 0 };
    }
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
  };

  return (
    <div className={classes.root}>
      {/* Search */}
      <div className={classes.searchContainer}>
        <InputBase
          ref={searchInputRef}
          className={classes.searchInput}
          placeholder={i18n.t("tickets.search.placeholder")}
          value={searchParam}
          onChange={handleSearch}
          startAdornment={
            <SearchIcon style={{ color: "#999", marginRight: 8, fontSize: 20 }} />
          }
        />
      </div>

      {/* Tabs */}
      {tab !== "search" && (
        <div className={classes.tabsContainer}>
          <Tabs
            value={tabOpen}
            onChange={handleChangeTabOpen}
            variant="fullWidth"
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab
              value="open"
              label={
                <Badge
                  className={classes.tabBadge}
                  badgeContent={openCount}
                  color="primary"
                  max={999}
                >
                  Atendendo
                </Badge>
              }
            />
            <Tab
              value="pending"
              label={
                <Badge
                  className={classes.tabBadge}
                  badgeContent={pendingCount}
                  color="secondary"
                  max={999}
                >
                  Aguardando
                </Badge>
              }
            />
            {user?.allowGroup && (
              <Tab
                value="group"
                label={
                  <Badge
                    className={classes.tabBadge}
                    badgeContent={groupCount}
                    color="primary"
                    max={999}
                  >
                    Grupos
                  </Badge>
                }
              />
            )}
            <Tab
              value="closed"
              label="Fechados"
            />
          </Tabs>
        </div>
      )}

      {/* Ticket list */}
      <div className={classes.ticketListWrapper}>
        {tab === "search" ? (
          <TicketsList
            searchParam={searchParam}
            showAll={user?.profile === "admin"}
            selectedQueueIds={selectedQueueIds}
            updateCount={() => {}}
            style={{ flex: 1, height: "100%" }}
          />
        ) : (
          <>
            <TabPanel value={tabOpen} name="open" style={{ height: "100%" }}>
              <TicketsList
                status="open"
                showAll={false}
                selectedQueueIds={selectedQueueIds}
                updateCount={(val) => setOpenCount(val)}
              />
            </TabPanel>
            <TabPanel value={tabOpen} name="pending" style={{ height: "100%" }}>
              <TicketsList
                status="pending"
                showAll={false}
                selectedQueueIds={selectedQueueIds}
                updateCount={(val) => setPendingCount(val)}
              />
            </TabPanel>
            {user?.allowGroup && (
              <TabPanel value={tabOpen} name="group" style={{ height: "100%" }}>
                <TicketsList
                  status="group"
                  showAll={false}
                  selectedQueueIds={selectedQueueIds}
                  updateCount={(val) => setGroupCount(val)}
                />
              </TabPanel>
            )}
            <TabPanel value={tabOpen} name="closed" style={{ height: "100%" }}>
              <TicketsList
                status="closed"
                showAll={false}
                selectedQueueIds={selectedQueueIds}
                updateCount={() => {}}
              />
            </TabPanel>
          </>
        )}
      </div>

      {/* FAB - New ticket */}
      <Fab
        color="primary"
        className={classes.fab}
        onClick={() => setNewTicketModalOpen(true)}
      >
        <AddIcon />
      </Fab>

      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={handleCloseOrOpenTicket}
      />
    </div>
  );
};

export default MobileTicketList;
